import { useQuery } from '@tanstack/react-query';
import {
  usePublicCoursesService,
  usePublicKnowledgeBaseService,
  useResourceSettingsService,
  ResourceMediaStatusIds,
} from '@actschurch/ccms-sdk';
import type { PublicResourceSettings, ResourceMediaView } from '@actschurch/ccms-sdk';
import { useOptionalAuth } from '@/hooks/useOptionalAuth';

const POLL_MS = 60_000;

/** True while the media is queued or processing (worth polling for). */
export function isMediaInFlight(media?: ResourceMediaView | null): boolean {
  const id = media?.mediaStatusID;
  return id === ResourceMediaStatusIds.Queued || id === ResourceMediaStatusIds.Processing;
}

/**
 * Streaming media (HLS, captions, transcript, listen-mode audio) for one
 * course session. Progressive enhancement only: any error resolves to null
 * so the player carries on with the session's original file. Polls once a
 * minute while the media is still being processed, otherwise stays put.
 */
export function useSessionMedia(
  courseId: number | null | undefined,
  sessionId: number | null | undefined,
  enabled = true,
) {
  const publicService = usePublicCoursesService();
  const { isAuthenticated } = useOptionalAuth();
  return useQuery<ResourceMediaView | null>({
    queryKey: ['public', 'session-media', courseId, sessionId, isAuthenticated],
    queryFn: async () => {
      try {
        return await publicService.getSessionMedia(courseId as number, sessionId as number);
      } catch {
        return null;
      }
    },
    enabled: enabled && !!courseId && !!sessionId,
    // Keep the same session's media while the key changes for another reason
    // (e.g. sign-in), so the player is not held again; never borrow another
    // session's media.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === courseId && previousQuery?.queryKey[3] === sessionId ? previous : undefined,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: (query) => (isMediaInFlight(query.state.data) ? POLL_MS : false),
  });
}

/**
 * Media for every media step of a KB article, keyed by kbStepID. Errors
 * resolve to an empty map so steps render exactly as before.
 */
export function useArticleStepsMedia(articleId: number | null | undefined, enabled = true) {
  const publicKb = usePublicKnowledgeBaseService();
  const { isAuthenticated } = useOptionalAuth();
  return useQuery<Map<number, ResourceMediaView>>({
    queryKey: ['public', 'kb-steps-media', articleId, isAuthenticated],
    queryFn: async () => {
      const map = new Map<number, ResourceMediaView>();
      try {
        const rows = await publicKb.getArticleStepsMedia(articleId as number);
        for (const row of rows ?? []) {
          if (row?.media) map.set(row.kbStepID, row.media);
        }
      } catch {
        /* no media: steps keep their original files */
      }
      return map;
    },
    enabled: enabled && !!articleId,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * Anonymous-safe Resource Centre settings (media switches included when the
 * backend sends them). Null on any error; callers treat a missing flag as on.
 */
export function usePublicResourceSettings() {
  const settingsService = useResourceSettingsService();
  return useQuery<PublicResourceSettings | null>({
    queryKey: ['public', 'resource-settings'],
    queryFn: async () => {
      try {
        return await settingsService.getPublicSettings();
      } catch {
        return null;
      }
    },
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Fetch and keep a text file from the media bucket (WebVTT or plain-text
 * transcript). The bucket allows any origin, so a plain CORS fetch works and
 * the <video> element itself never needs crossOrigin.
 */
export function useRemoteText(url: string | null | undefined) {
  return useQuery<string | null>({
    queryKey: ['media-text', url],
    queryFn: async () => {
      try {
        const res = await fetch(url as string, { mode: 'cors', credentials: 'omit' });
        if (!res.ok) return null;
        return await res.text();
      } catch {
        return null;
      }
    },
    enabled: !!url,
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
